/**
 * Atelier Royal: style-gallery.js: <ar-style-gallery> (sections/royal-style-gallery.liquid).
 *
 * - Swipe counter for the mobile/tablet rail ("3 / 7"). The rail itself is CSS scroll-snap:
 *   user-driven only, never auto-advancing. The counter template comes translated from Liquid
 *   (data-template with [current] and [total]); it is decorative (aria-hidden).
 * - Optional (data-verify-styles="true"): hides styles the creator backend cannot paint,
 *   using GET <theme.creator.apiBase>/styles → { ok, handles: [...] }. Any failure keeps
 *   every style visible. Skipped in the Theme Editor.
 */
import { theme } from '@theme/global';

class ARStyleGallery extends HTMLElement {
  connectedCallback() {
    if (this.dataset.arReady) return;
    this.dataset.arReady = 'true';
    this.rail = this.querySelector('[data-style-rail]');
    this.counter = this.querySelector('[data-style-counter]');
    this.setupCounter();
    if (this.dataset.verifyStyles === 'true' && !theme.designMode) this.verifyStyles();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  get cards() {
    return this.rail ? [...this.rail.children].filter((card) => !card.hidden) : [];
  }

  setupCounter() {
    this.observer?.disconnect();
    if (!this.rail || !this.counter || !('IntersectionObserver' in window)) return;
    const cards = this.cards;
    const hint = this.counter.closest('.ar-style-gallery__hint');
    if (hint) hint.hidden = cards.length < 2;
    if (cards.length < 2) return;

    const ratios = new Map();
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => ratios.set(entry.target, entry.intersectionRatio));
        let current = 0;
        let best = -1;
        cards.forEach((card, index) => {
          const ratio = ratios.get(card) ?? 0;
          if (ratio > best + 0.01) {
            best = ratio;
            current = index;
          }
        });
        this.renderCounter(current + 1, cards.length);
      },
      { root: this.rail, threshold: [0, 0.35, 0.65, 1] }
    );
    cards.forEach((card) => this.observer.observe(card));
    this.renderCounter(1, cards.length);
  }

  renderCounter(current, total) {
    const template = this.counter.dataset.template || '[current] / [total]';
    this.counter.textContent = template.replace('[current]', String(current)).replace('[total]', String(total));
  }

  async verifyStyles() {
    const base = theme.creator?.apiBase || '/apps/atelier';
    try {
      const response = await fetch(`${base}/styles`, { headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      const data = await response.json();
      const handles = Array.isArray(data?.handles) ? data.handles : [];
      if (!handles.length) return;
      const cards = [...this.querySelectorAll('[data-style-handle]')];
      const supported = cards.filter((card) => handles.includes(card.dataset.styleHandle));
      if (!supported.length) return; // never empty the wall because of a mismatch
      cards.forEach((card) => {
        card.hidden = !handles.includes(card.dataset.styleHandle);
      });
      this.setupCounter();
    } catch {
      /* keep every style visible */
    }
  }
}

if (!customElements.get('ar-style-gallery')) customElements.define('ar-style-gallery', ARStyleGallery);
